/**
 * BookingNewScreen.jsx
 * Full booking flow: Service → Trainer → Calendar → Time → Confirm
 * With real backend integration and Waitlist support
 */
import React, { useState, useEffect } from 'react';
import { 
  ChevronRight, ChevronLeft, Calendar, Clock, User, Check, RefreshCw, 
  MapPin, CreditCard, Mail, MessageCircle, Info, Bell, Sun, Sunset, Moon, Zap, X
} from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper functions
const formatCurrency = (amount) => `CHF ${(amount || 0).toFixed(2)}`;

// Category icon helper
const getCategoryIcon = (categoryName) => {
  const name = categoryName?.toLowerCase() || '';
  if (name.includes('ems')) return '⚡';
  if (name.includes('personal')) return '🏋️';
  if (name.includes('kryo') || name.includes('sauna')) return '❄️';
  if (name.includes('massage')) return '💆';
  if (name.includes('yoga')) return '🧘';
  if (name.includes('group') || name.includes('gruppe')) return '👥';
  return '📅';
};

const getDayName = (date) => {
  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  return days[date.getDay()];
};

const getMonthName = (date) => {
  const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 
                  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  return months[date.getMonth()];
};

const isSameDay = (d1, d2) => {
  if (!d1 || !d2) return false;
  return d1.getDate() === d2.getDate() && 
         d1.getMonth() === d2.getMonth() && 
         d1.getFullYear() === d2.getFullYear();
};

// Helper to format date as YYYY-MM-DD without timezone issues
const formatDateLocal = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const BookingNewScreen = ({ 
  onBack, 
  services = [], 
  trainers = [],
  portalToken,
  onBook,
  customerData
}) => {
  const { t } = useTranslation();
  // Steps: 1=Category, 2=Service, 3=Variant (if needed), 4=Trainer, 5=Calendar/Time, 6=Confirm
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null); // NEW: For services with variants
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [monthAvailability, setMonthAvailability] = useState({});
  
  // Time slots
  const [availability, setAvailability] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingMonth, setLoadingMonth] = useState(false);
  
  // Booking
  const [booking, setBooking] = useState(false);
  const [lastBookedAppointment, setLastBookedAppointment] = useState(null);
  const [showAddonPopup, setShowAddonPopup] = useState(false);
  const [addonAvailability, setAddonAvailability] = useState(null);
  const [loadingAddon, setLoadingAddon] = useState(false);
  const [bookingAddon, setBookingAddon] = useState(false);
  
  // Series booking state
  const [isSeriesMode, setIsSeriesMode] = useState(false);
  const [seriesFrequency, setSeriesFrequency] = useState('weekly');
  const [seriesIntervalWeeks, setSeriesIntervalWeeks] = useState(1);
  const [seriesWeekdays, setSeriesWeekdays] = useState([]);
  const [showSeriesResult, setShowSeriesResult] = useState(null);
  
  // Options
  const [sendEmail, setSendEmail] = useState(true);
  const [sendWhatsApp, setSendWhatsApp] = useState(true);
  const [notes, setNotes] = useState('');
  
  // Waitlist states
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistPreferences, setWaitlistPreferences] = useState(['any']);
  const [waitlistPreferredDays, setWaitlistPreferredDays] = useState(['daily']);
  const [waitlistDateMode, setWaitlistDateMode] = useState('recurring'); // 'recurring' or 'specific'
  const [waitlistSpecificDate, setWaitlistSpecificDate] = useState('');
  const [waitlistAutoBook, setWaitlistAutoBook] = useState(false);
  const [waitlistAutoBookHours, setWaitlistAutoBookHours] = useState(6);
  const [customTimeStart, setCustomTimeStart] = useState('09:00');
  const [customTimeEnd, setCustomTimeEnd] = useState('18:00');
  const [customTimeRanges, setCustomTimeRanges] = useState([]);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [isOnWaitlist, setIsOnWaitlist] = useState(false);
  
  // Weekday options
  const weekdayOptions = [
    { value: 'daily', label: 'Täglich' },
    { value: 'monday', label: 'Mo' },
    { value: 'tuesday', label: 'Di' },
    { value: 'wednesday', label: 'Mi' },
    { value: 'thursday', label: 'Do' },
    { value: 'friday', label: 'Fr' },
    { value: 'saturday', label: 'Sa' },
  ];

  // Group services by category
  const categories = React.useMemo(() => {
    const categoryMap = {};
    services.forEach(service => {
      // Use service.category first, then fallback
      const catName = service.category || service.category_name || 'Weitere Services';
      if (!categoryMap[catName]) {
        categoryMap[catName] = {
          name: catName,
          services: [],
          icon: getCategoryIcon(catName)
        };
      }
      categoryMap[catName].services.push(service);
    });
    return Object.values(categoryMap);
  }, [services]);

  // Get services for selected category
  const filteredServices = selectedCategory 
    ? categories.find(c => c.name === selectedCategory)?.services || []
    : services;

  // Generate calendar days for current month
  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Add empty days for padding (start from Monday)
    let startPadding = firstDay.getDay() - 1;
    if (startPadding < 0) startPadding = 6; // Sunday
    
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    // Add actual days
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      const isPast = date < today;
      const dateStr = formatDateLocal(date);
      const hasSlots = monthAvailability[dateStr]?.available > 0;
      
      days.push({
        date,
        day: d,
        isPast,
        hasSlots,
        isToday: isSameDay(date, today),
        isSelected: isSameDay(date, selectedDate)
      });
    }
    
    return days;
  };

  // Load month availability
  useEffect(() => {
    if (selectedService && step === 5) {
      loadMonthAvailability();
    }
  }, [selectedService, selectedTrainer, currentMonth, step]);

  // Load day availability when date selected
  useEffect(() => {
    if (selectedService && selectedDate && step === 5) {
      loadDayAvailability();
    }
  }, [selectedDate]);

  const loadMonthAvailability = async () => {
    if (!portalToken) return;
    
    setLoadingMonth(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      
      const params = {
        service_id: selectedService.id,
        year,
        month
      };
      if (selectedTrainer) {
        params.trainer_id = selectedTrainer.id;
      }
      
      const response = await axios.get(`${API}/portal/booking/availability/month`, {
        params,
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      
      // Convert array to object keyed by date
      const availByDate = {};
      (response.data.days || response.data || []).forEach(day => {
        availByDate[day.date] = {
          available: day.available_slots || day.slots || 0,
          times: day.times || []
        };
      });
      
      setMonthAvailability(availByDate);
    } catch (error) {
      console.error('Error loading month availability:', error);
      setMonthAvailability({});
    } finally {
      setLoadingMonth(false);
    }
  };

  const loadDayAvailability = async () => {
    if (!portalToken || !selectedDate) return;
    
    setLoadingSlots(true);
    try {
      const dateStr = formatDateLocal(selectedDate);
      const params = {
        service_id: selectedService.id,
        date: dateStr
      };
      if (selectedTrainer) {
        params.trainer_id = selectedTrainer.id;
      }
      
      const response = await axios.get(`${API}/portal/booking/availability`, {
        params,
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      
      // Combine all available slots from different time periods
      const data = response.data;
      const allSlots = [
        ...(data.morning_slots || []),
        ...(data.afternoon_slots || []),
        ...(data.evening_slots || []),
        ...(data.slots || [])
      ].filter(slot => slot.available !== false);
      
      // Sort slots by time
      allSlots.sort((a, b) => {
        const timeA = a.start_time || a.time || '';
        const timeB = b.start_time || b.time || '';
        return timeA.localeCompare(timeB);
      });
      
      setAvailability(allSlots);
    } catch (error) {
      console.error('Error loading availability:', error);
      setAvailability([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Navigate months
  const prevMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    // Don't go before current month
    const today = new Date();
    if (newMonth.getMonth() >= today.getMonth() || newMonth.getFullYear() > today.getFullYear()) {
      setCurrentMonth(newMonth);
    }
  };

  const nextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    // Limit to 3 months ahead
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    if (newMonth <= maxDate) {
      setCurrentMonth(newMonth);
    }
  };

  // Handle booking
  const toggleWeekday = (idx) => {
    setSeriesWeekdays(prev => prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx]);
  };

  const handleBook = async () => {
    if (!selectedService || !selectedSlot) {
      toast.error('Bitte wählen Sie Service und Zeitslot');
      return;
    }

    setBooking(true);
    try {
      const dateStr = formatDateLocal(selectedDate);

      if (isSeriesMode) {
        // Series booking
        const seriesConfig = {
          frequency: seriesFrequency,
          interval_weeks: seriesFrequency === 'custom' ? seriesIntervalWeeks : (seriesFrequency === 'biweekly' ? 2 : 1),
          weekdays: seriesWeekdays.length > 0 ? seriesWeekdays : null,
          occurrences: 300
        };
        const response = await axios.post(
          `${API}/portal/booking/book-series`,
          {
            service_id: selectedService.id,
            date: dateStr,
            time: selectedSlot.time,
            trainer_id: selectedTrainer?.id || null,
            series_config: seriesConfig,
            selected_variant: selectedVariant || null
          },
          { headers: { Authorization: `Bearer ${portalToken}` } }
        );
        setShowSeriesResult(response.data);
        const count = response.data.appointments_created;
        toast.success(count > 50 ? 'Serienbuchung erfolgreich!' : `${count} Termine gebucht!`);
        setIsSeriesMode(false);
      } else {
        // Single booking
        let startTime = selectedSlot.datetime || selectedSlot.start_time;
        if (!startTime && selectedSlot.time && selectedDate) {
          startTime = `${dateStr}T${selectedSlot.time}:00`;
        }
        
        const bookingData = {
          service_id: selectedService.id,
          start_time: startTime,
          trainer_id: selectedTrainer?.id,
          send_confirmation_email: sendEmail,
          send_whatsapp: sendWhatsApp,
          notes: notes,
          selected_variant: selectedVariant
        };

        const result = await onBook(bookingData);
        if (result && (result.appointment || result.appointment_id || result.id)) {
          toast.success('Termin erfolgreich gebucht!');
          const appointmentData = result.appointment || result;
          const bookedTrainerId = appointmentData.trainer_id || selectedTrainer?.id;
          
          setLastBookedAppointment({
            id: appointmentData.id || result.appointment_id || result.id,
            service_id: selectedService.id,
            service_name: selectedService.name,
            start_time: appointmentData.start_time || startTime,
            end_time: appointmentData.end_time,
            duration_minutes: selectedService?.duration_minutes || selectedService?.duration || 60,
            date: dateStr,
            time: formatSlotTime(selectedSlot),
            trainer_id: bookedTrainerId
          });
          checkAddonAvailability(selectedService.id, bookedTrainerId, dateStr, formatSlotTime(selectedSlot));
        } else {
          toast.success('Termin erfolgreich gebucht!');
          setTimeout(() => onBack(), 1500);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Buchung fehlgeschlagen');
    } finally {
      setBooking(false);
    }
  };

  const formatGoogleDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const buildCalendarEventLinks = (appointment) => {
    if (!appointment) return null;
    if (!portalToken) return null;
    const start = appointment.start_time ? new Date(appointment.start_time) : null;
    if (!start) return null;
    const end = appointment.end_time ? new Date(appointment.end_time) : new Date(start.getTime() + (appointment.duration_minutes || 60) * 60000);
    const titleBase = appointment.service_name || 'Training';
    const title = appointment.trainer_id ? `${titleBase} mit Trainer` : titleBase;
    const details = [`Training: ${titleBase}`].join('\n');

    const googleDates = `${formatGoogleDate(start)}/${formatGoogleDate(end)}`;
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${googleDates}&details=${encodeURIComponent(details)}`;
    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(title)}&startdt=${encodeURIComponent(start.toISOString())}&enddt=${encodeURIComponent(end.toISOString())}&body=${encodeURIComponent(details)}`;

    const icsUrl = `${API}/portal/appointments/${appointment.id}/calendar?token=${portalToken}`;
    const appleUrl = icsUrl.replace('https://', 'webcal://').replace('http://', 'webcal://');

    return { googleUrl, outlookUrl, appleUrl };
  };
  
  // Check addon availability after booking
  const checkAddonAvailability = async (serviceId, trainerId, date, time) => {
    setLoadingAddon(true);
    console.log('Checking addon availability:', { serviceId, trainerId, date, time, hasToken: !!portalToken });
    try {
      const response = await axios.get(`${API}/portal/addon-services/check-availability`, {
        params: {
          main_service_id: serviceId,
          trainer_id: trainerId,
          booking_date: date,
          booking_time: time
        },
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      
      console.log('Addon availability response:', response.data);
      setAddonAvailability(response.data);
      if (response.data?.available) {
        setShowAddonPopup(true);
      } else {
        // No addon available, show success message and go back
        toast.success('✅ Buchung abgeschlossen');
        setTimeout(() => onBack(), 1500);
      }
    } catch (error) {
      console.error('Check addon error:', error.response?.data || error.message);
      // On error, show success for main booking and go back
      toast.success('✅ Buchung abgeschlossen');
      setTimeout(() => onBack(), 1500);
    } finally {
      setLoadingAddon(false);
    }
  };
  
  // Book addon service
  const bookAddonService = async () => {
    if (!addonAvailability?.available || !lastBookedAppointment) return;
    
    setBookingAddon(true);
    try {
      const response = await axios.post(`${API}/portal/booking/book`, {
        service_id: addonAvailability.addon_service.id,
        trainer_id: addonAvailability.trainer_id,
        date: lastBookedAppointment.date,
        time: addonAvailability.addon_start_time,
        // Don't put addon info in notes - use dedicated fields instead
        is_addon: true,
        main_appointment_time: lastBookedAppointment.time,
        parent_appointment_id: lastBookedAppointment.appointment_id,
        addon_price: addonAvailability.combo_price,
        send_confirmation_email: sendEmail,
        send_whatsapp: sendWhatsApp
      }, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      
      toast.success(`✅ ${addonAvailability.addon_service.name} hinzugefügt!`);
      setShowAddonPopup(false);
      onBack();
    } catch (error) {
      console.error('Book addon error:', error);
      toast.error(error.response?.data?.detail || 'Add-on Buchung fehlgeschlagen');
    } finally {
      setBookingAddon(false);
    }
  };
  
  // Toggle waitlist time preference
  const toggleWaitlistPreference = (pref) => {
    if (pref === 'any') {
      setWaitlistPreferences(['any']);
    } else {
      let newPrefs = waitlistPreferences.filter(p => p !== 'any');
      if (newPrefs.includes(pref)) {
        newPrefs = newPrefs.filter(p => p !== pref);
      } else {
        newPrefs.push(pref);
      }
      if (newPrefs.length === 0) newPrefs = ['any'];
      setWaitlistPreferences(newPrefs);
    }
  };
  
  // Add custom time range
  const addCustomTimeRange = () => {
    const range = `${customTimeStart}-${customTimeEnd}`;
    if (!customTimeRanges.includes(range)) {
      setCustomTimeRanges([...customTimeRanges, range]);
    }
  };
  
  // Handle joining waitlist
  const handleJoinWaitlist = async () => {
    if (!selectedService || !portalToken) {
      toast.error('Bitte wählen Sie einen Service');
      return;
    }
    
    setJoiningWaitlist(true);
    try {
      const payload = {
        service_id: selectedService.id,
        trainer_id: selectedTrainer?.id,
        time_preferences: waitlistPreferences,
        custom_time_ranges: customTimeRanges,
        preferred_days: waitlistDateMode === 'recurring' ? waitlistPreferredDays : [],
        specific_date: waitlistDateMode === 'specific' ? waitlistSpecificDate : null,
        auto_book: waitlistAutoBook,
        auto_book_hours: waitlistAutoBook ? waitlistAutoBookHours : null,
        recurring: waitlistDateMode === 'recurring'
      };
      
      await axios.post(
        `${API}/portal/waitlist`,
        payload,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      toast.success('✅ Auf die Warteliste gesetzt!');
      setIsOnWaitlist(true);
      setShowWaitlistModal(false);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Hinzufügen zur Warteliste';
      toast.error(msg);
    } finally {
      setJoiningWaitlist(false);
    }
  };

  // Format time from slot
  const formatSlotTime = (slot) => {
    const time = slot.start_time || slot.time;
    if (!time) return '';
    if (typeof time === 'string' && time.includes('T')) {
      return time.split('T')[1]?.substring(0, 5);
    }
    if (typeof time === 'string' && time.includes(':')) {
      return time.substring(0, 5);
    }
    return new Date(time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  // Step progress bar
  const renderProgress = () => (
    <div className="flex gap-1.5 mb-4">
      {[1, 2, 3, 4, 5].map(s => (
        <div 
          key={s} 
          className={`flex-1 h-1 rounded-full transition-colors ${
            s <= step ? 'bg-yellow-500' : 'bg-zinc-700'
          }`} 
        />
      ))}
    </div>
  );

  const mobileCalendarLinks = lastBookedAppointment ? buildCalendarEventLinks(lastBookedAppointment) : null;

  return (
    <div className="p-4 space-y-3 h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-3 pt-2">
        <button 
          onClick={() => step > 1 ? setStep(step - 1) : onBack()} 
          className="w-8 h-8 bg-zinc-800 rounded-lg flex items-center justify-center"
        >
          <ChevronLeft className="w-4 h-4 text-zinc-400" />
        </button>
        <h1 className="text-white text-base font-bold">
          {step === 1 && 'Kategorie wählen'}
          {step === 2 && 'Service auswählen'}
          {step === 3 && 'Variante wählen'}
          {step === 4 && 'Trainer auswählen'}
          {step === 5 && 'Datum & Zeit'}
          {step === 6 && 'Bestätigung'}
        </h1>
      </div>

      {renderProgress()}

      {/* Step 1: Category Selection */}
      {step === 1 && (
        <div className="space-y-2">
          {categories.length === 0 ? (
            <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
              <Calendar className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">{t('bookings.no_categories')}</p>
            </div>
          ) : (
            categories.map((category) => (
              <button 
                key={category.name}
                onClick={() => { setSelectedCategory(category.name); setStep(2); }}
                className="w-full bg-zinc-800/50 hover:bg-zinc-800 rounded-xl p-4 text-left flex items-center gap-4 transition-colors"
              >
                <div className="w-14 h-14 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                  <span className="text-2xl">{category.icon}</span>
                </div>
                <div className="flex-1">
                  <p className="text-white text-base font-medium">{category.name}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">
                    {category.services.length} {category.services.length === 1 ? 'Service' : 'Services'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-500" />
              </button>
            ))
          )}
        </div>
      )}

      {/* Step 2: Service Selection */}
      {step === 2 && (
        <div className="space-y-2">
          {/* Selected Category */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">{getCategoryIcon(selectedCategory)}</span>
              <p className="text-yellow-400 text-sm font-medium">{selectedCategory}</p>
            </div>
          </div>

          {filteredServices.length === 0 ? (
            <div className="bg-zinc-800/50 rounded-xl p-6 text-center">
              <Calendar className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">{t('bookings.no_services')}</p>
            </div>
          ) : (
            filteredServices.map((service) => (
              <button 
                key={service.id}
                onClick={() => { 
                  setSelectedService(service);
                  setSelectedVariant(null);
                  // If service has variants, go to variant selection (step 3), otherwise skip to trainer (step 4)
                  if (service.variants && service.variants.length > 0) {
                    setStep(3);
                  } else {
                    setStep(4);
                  }
                }}
                className="w-full bg-zinc-800/50 hover:bg-zinc-800 rounded-xl p-3 text-left flex items-center gap-3 transition-colors"
              >
                <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-yellow-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{service.name}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">
                    {service.duration_minutes || service.duration} Min.
                    {service.variants && service.variants.length > 0 && (
                      <span className="ml-2 text-blue-400">• {service.variants.length} Varianten</span>
                    )}
                  </p>
                </div>
                <div className="text-right flex items-center gap-2">
                  {/* Only show price if > 0 or has points */}
                  {(service.points_cost > 0 || service.price > 0) && (
                    <p className="text-yellow-400 font-medium text-sm">
                      {service.points_cost ? `${service.points_cost} P` : formatCurrency(service.price)}
                    </p>
                  )}
                  <ChevronRight className="w-4 h-4 text-zinc-500" />
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Step 3: Variant Selection (only if service has variants) */}
      {step === 3 && selectedService?.variants && selectedService.variants.length > 0 && (
        <div className="space-y-2">
          {/* Selected Service Summary */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-yellow-400" />
              <p className="text-yellow-400 text-sm font-medium">{selectedService?.name}</p>
            </div>
          </div>

          <p className="text-zinc-400 text-sm mb-3">{t('bookings.which_variant')}</p>

          {selectedService.variants.map((variant, idx) => (
            <button 
              key={idx}
              onClick={() => { setSelectedVariant(variant); setStep(4); }}
              className="w-full bg-zinc-800/50 hover:bg-zinc-800 rounded-xl p-4 text-left flex items-center gap-3 transition-colors"
            >
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">{variant}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-500" />
            </button>
          ))}
        </div>
      )}

      {/* Step 4: Trainer Selection */}
      {step === 4 && (
        <div className="space-y-2">
          {/* Selected Service Summary */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-yellow-400" />
              <p className="text-yellow-400 text-sm font-medium">
                {selectedService?.name}
                {selectedVariant && <span className="text-blue-400 ml-2">• {selectedVariant}</span>}
              </p>
            </div>
          </div>

          {/* No preference option */}
          <button 
            onClick={() => { setSelectedTrainer(null); setStep(5); }}
            className="w-full bg-zinc-800/50 hover:bg-zinc-800 rounded-xl p-3 text-left flex items-center gap-3"
          >
            <div className="w-12 h-12 bg-zinc-700 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-zinc-400" />
            </div>
            <div className="flex-1">
              <p className="text-white text-sm font-medium">{t('bookings.no_trainer_preferred')}</p>
              <p className="text-zinc-500 text-xs">{t('bookings.first_available')}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
          </button>

          {/* Trainer list */}
          {trainers.map((trainer) => (
            <button 
              key={trainer.id}
              onClick={() => { setSelectedTrainer(trainer); setStep(5); }}
              className="w-full bg-zinc-800/50 hover:bg-zinc-800 rounded-xl p-3 text-left flex items-center gap-3"
            >
              <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <span className="text-yellow-400 font-bold text-lg">
                  {(trainer.name || trainer.first_name || 'T').charAt(0)}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">
                  {trainer.name || `${trainer.first_name} ${trainer.last_name}`}
                </p>
                <p className="text-zinc-500 text-xs">{trainer.specialization || 'Personal Trainer'}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-500" />
            </button>
          ))}
        </div>
      )}

      {/* Step 5: Calendar & Time Selection */}
      {step === 5 && (
        <div className="space-y-3">
          {/* Calendar Header */}
          <div className="flex items-center justify-between">
            <button 
              onClick={prevMonth}
              className="p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700"
            >
              <ChevronLeft className="w-4 h-4 text-zinc-400" />
            </button>
            <div className="text-center">
              <p className="text-white font-medium">
                {getMonthName(currentMonth)} {currentMonth.getFullYear()}
              </p>
              {loadingMonth && (
                <RefreshCw className="w-3 h-3 animate-spin text-yellow-500 mx-auto mt-1" />
              )}
            </div>
            <button 
              onClick={nextMonth}
              className="p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700"
            >
              <ChevronRight className="w-4 h-4 text-zinc-400" />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
              <div key={day} className="text-zinc-500 text-[10px] py-1">{day}</div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {getCalendarDays().map((day, i) => {
              if (!day) {
                return <div key={i} className="aspect-square" />;
              }
              
              const isSelectable = !day.isPast && day.hasSlots;
              
              return (
                <button
                  key={i}
                  onClick={() => isSelectable && setSelectedDate(day.date)}
                  disabled={day.isPast || !day.hasSlots}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center text-xs transition-all ${
                    day.isSelected
                      ? 'bg-yellow-500 text-black font-bold'
                      : day.isToday
                      ? 'bg-yellow-500/20 text-yellow-400'
                      : isSelectable
                      ? 'bg-zinc-800 text-white hover:bg-zinc-700'
                      : 'bg-zinc-900/50 text-zinc-600'
                  }`}
                >
                  <span>{day.day}</span>
                  {day.hasSlots && !day.isPast && (
                    <span className={`w-1 h-1 rounded-full mt-0.5 ${
                      day.isSelected ? 'bg-black' : 'bg-yellow-500'
                    }`} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Time Slots */}
          {selectedDate && (
            <div className="space-y-2 mt-4">
              <p className="text-zinc-400 text-xs">
                Verfügbare Zeiten am {selectedDate.getDate()}. {getMonthName(selectedDate)}
              </p>
              
              {loadingSlots ? (
                <div className="flex justify-center py-4">
                  <RefreshCw className="w-5 h-5 animate-spin text-yellow-500" />
                </div>
              ) : availability.length === 0 ? (
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center space-y-3">
                  <p className="text-zinc-400 text-sm">{t('bookings.no_slots')}</p>
                  
                  {/* Waitlist Button */}
                  {portalToken && !isOnWaitlist && (
                    <button
                      onClick={() => setShowWaitlistModal(true)}
                      className="w-full py-3 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-xl text-purple-300 font-medium flex items-center justify-center gap-2 hover:from-purple-500/30 hover:to-blue-500/30 transition-all"
                    >
                      <Bell className="w-4 h-4" />
                      Auf Warteliste setzen
                    </button>
                  )}
                  
                  {isOnWaitlist && (
                    <div className="flex items-center justify-center gap-2 text-yellow-400 text-sm">
                      <Check className="w-4 h-4" />
                      Du bist auf der Warteliste
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                  {availability.map((slot, i) => {
                    const timeStr = formatSlotTime(slot);
                    const isSelected = selectedSlot === slot;
                    
                    return (
                      <button 
                        key={i}
                        onClick={() => { setSelectedSlot(slot); setStep(6); }}
                        className={`py-2.5 rounded-lg text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-yellow-500 text-black' 
                            : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                        }`}
                      >
                        {timeStr}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Waitlist Option - shown at bottom of Step 5 */}
          {portalToken && !isOnWaitlist && (
            <div className="mt-4 pt-4 border-t border-zinc-700">
              <p className="text-zinc-400 text-xs mb-2">{t('bookings.no_matching_slot')}</p>
              <button
                onClick={() => setShowWaitlistModal(true)}
                className="w-full py-3 bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-xl text-purple-300 font-medium flex items-center justify-center gap-2 hover:from-purple-500/30 hover:to-blue-500/30 transition-all"
              >
                <Bell className="w-4 h-4" />
                Auf Warteliste setzen
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 6: Confirmation */}
      {step === 6 && (
        <div className="space-y-2">
          {/* Booking Summary - Compact */}
          <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 rounded-lg p-3 border border-yellow-500/30">
            <p className="text-yellow-400 text-xs font-medium mb-2">{t('bookings.summary')}</p>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {selectedService?.name}
                    {selectedVariant && <span className="text-blue-400 ml-1 text-xs">• {selectedVariant}</span>}
                  </p>
                  <p className="text-zinc-400 text-xs">{selectedService?.duration_minutes || selectedService?.duration} Min</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                <div>
                  <p className="text-white text-sm">
                    {getDayName(selectedDate)}, {selectedDate?.getDate()}. {getMonthName(selectedDate)} • <span className="text-yellow-400 font-medium">{formatSlotTime(selectedSlot)} Uhr</span>
                  </p>
                </div>
              </div>
              
              {selectedTrainer && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                  <p className="text-white text-sm">
                    {selectedTrainer.name || `${selectedTrainer.first_name} ${selectedTrainer.last_name}`}
                  </p>
                </div>
              )}
              
              {/* Only show price if > 0 or points */}
              {(selectedService?.points_cost > 0 || selectedService?.price > 0) && (
                <div className="flex items-center gap-2 pt-1.5 border-t border-yellow-500/30">
                  <CreditCard className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                  <p className="text-white text-sm font-bold">
                    {selectedService?.points_cost 
                      ? `${selectedService.points_cost} Punkte`
                      : formatCurrency(selectedService?.price)
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Notification Options - Compact */}
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <p className="text-white text-xs font-medium flex items-center gap-1.5 mb-2">
              <Bell className="w-3.5 h-3.5 text-yellow-400" />
              Benachrichtigungen
              <span className="text-zinc-500 text-xs font-normal">(min. 1)</span>
            </p>
            
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={sendEmail} 
                  onChange={(e) => {
                    // Ensure at least one is selected
                    if (!e.target.checked && !sendWhatsApp) {
                      setSendWhatsApp(true);
                    }
                    setSendEmail(e.target.checked);
                  }}
                  className="w-3.5 h-3.5 rounded bg-zinc-700 border-zinc-600 text-yellow-500 focus:ring-yellow-500"
                />
                <div className="flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5 text-zinc-400" />
                  <span className="text-zinc-300 text-xs">E-Mail</span>
                </div>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={sendWhatsApp} 
                  onChange={(e) => {
                    // Ensure at least one is selected
                    if (!e.target.checked && !sendEmail) {
                      setSendEmail(true);
                    }
                    setSendWhatsApp(e.target.checked);
                  }}
                  className="w-3.5 h-3.5 rounded bg-zinc-700 border-zinc-600 text-yellow-500 focus:ring-yellow-500"
                />
                <div className="flex items-center gap-1">
                  <MessageCircle className="w-3.5 h-3.5 text-zinc-400" />
                  <span className="text-zinc-300 text-xs">WhatsApp</span>
                </div>
              </label>
            </div>
          </div>

          {/* Notes - Compact */}
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <p className="text-white text-xs font-medium mb-1.5">{t('bookings.notes_label')}</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Besondere Wünsche..."
              className="w-full bg-zinc-700 rounded-lg p-2 text-white text-xs placeholder-zinc-500 resize-none h-12"
            />
          </div>

          {/* Series Booking Toggle */}
          <div className="bg-zinc-800/50 rounded-lg p-3" data-testid="series-toggle-section">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={isSeriesMode}
                onChange={(e) => {
                  setIsSeriesMode(e.target.checked);
                  if (e.target.checked && selectedDate) {
                    const dayIdx = (selectedDate.getDay() + 6) % 7;
                    setSeriesWeekdays(prev => prev.includes(dayIdx) ? prev : [dayIdx]);
                  }
                }}
                className="w-4 h-4 rounded bg-zinc-700 border-zinc-600 text-yellow-500 focus:ring-yellow-500"
                data-testid="series-mode-toggle"
              />
              <div>
                <span className="text-white text-xs font-medium">{t('bookings.series_title')}</span>
                <p className="text-zinc-500 text-[10px]">{t('bookings.series_desc')}</p>
              </div>
            </label>

            {isSeriesMode && (
              <div className="mt-3 space-y-3 pt-3 border-t border-zinc-700">
                {/* Frequency */}
                <div>
                  <p className="text-zinc-400 text-[10px] font-medium mb-1.5">{t('bookings.repeat')}</p>
                  <select
                    value={seriesFrequency}
                    onChange={(e) => setSeriesFrequency(e.target.value)}
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-2.5 py-2 text-white text-xs"
                    data-testid="series-frequency-select"
                  >
                    <option value="weekly">{t('bookings.weekly')}</option>
                    <option value="biweekly">{t('bookings.biweekly')}</option>
                    <option value="custom">{t('bookings.custom')}</option>
                  </select>
                </div>

                {seriesFrequency === 'custom' && (
                  <div>
                    <p className="text-zinc-400 text-[10px] font-medium mb-1.5">{t('bookings.every_x_weeks')}</p>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={seriesIntervalWeeks}
                      onChange={(e) => setSeriesIntervalWeeks(parseInt(e.target.value) || 1)}
                      className="w-20 bg-zinc-700 border border-zinc-600 rounded-lg px-2.5 py-2 text-white text-xs"
                      data-testid="series-interval-input"
                    />
                  </div>
                )}

                {/* Weekday Selection */}
                <div>
                  <p className="text-zinc-400 text-[10px] font-medium mb-1.5">{t('bookings.weekdays_optional')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day, idx) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleWeekday(idx)}
                        className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                          seriesWeekdays.includes(idx)
                            ? 'bg-yellow-500 text-black'
                            : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                        }`}
                        data-testid={`series-weekday-${idx}`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  <p className="text-zinc-500 text-[10px] mt-1">
                    Leer = gleicher Wochentag wie Startdatum
                  </p>
                </div>

                <div className="p-2 bg-yellow-500/10 rounded border border-yellow-500/20 text-[10px] text-yellow-400">
                  <strong>{t('bookings.series_note').split('.')[0]}.</strong> {t('bookings.series_note').split('. ').slice(1).join('. ')}
                </div>
              </div>
            )}
          </div>

          {/* Book Button */}
          <button 
            onClick={handleBook}
            disabled={booking}
            className="w-full py-3 bg-yellow-500 rounded-xl text-black font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="confirm-book-button"
          >
            {booking ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Wird gebucht...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {isSeriesMode ? 'Serie buchen' : 'Jetzt buchen'}
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Series Result Modal */}
      {showSeriesResult && (
        <div className="absolute inset-0 bg-black/95 z-50 flex flex-col" data-testid="series-result-modal">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Header */}
            <div className="text-center pt-6">
              <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3 ${
                showSeriesResult.appointments_skipped > 0 ? 'bg-orange-500/20' : 'bg-yellow-500/20'
              }`}>
                {showSeriesResult.appointments_skipped > 0 ? (
                  <Info className="w-7 h-7 text-orange-400" />
                ) : (
                  <Check className="w-7 h-7 text-yellow-400" />
                )}
              </div>
              <h2 className="text-white font-bold text-base">
                {showSeriesResult.appointments_skipped > 0 
                  ? 'Serie teilweise gebucht' 
                  : 'Serienbuchung erfolgreich!'}
              </h2>
            </div>

            {/* Created count */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-yellow-400" />
                <span className="text-zinc-300 text-sm">{t('bookings.booked_count')}</span>
              </div>
              <span className="text-yellow-400 font-bold text-lg" data-testid="series-created-count">
                {showSeriesResult.appointments_created > 50 
                  ? 'Alle Termine' 
                  : `${showSeriesResult.appointments_created} Termine`}
              </span>
            </div>

            {/* Skipped */}
            {showSeriesResult.appointments_skipped > 0 && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-orange-400" />
                    <span className="text-zinc-300 text-sm">{t('bookings.skipped_count')}</span>
                  </div>
                  <span className="text-orange-400 font-bold">{showSeriesResult.appointments_skipped}</span>
                </div>
                {showSeriesResult.skipped_dates?.length > 0 && (
                  <div className="bg-black/30 rounded-lg p-2 max-h-28 overflow-y-auto space-y-1">
                    {showSeriesResult.skipped_dates.map((dateInfo, idx) => (
                      <p key={idx} className="text-orange-300 text-xs">{dateInfo}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* First appointment */}
            {showSeriesResult.first_appointment && (
              <div className="bg-zinc-800/50 rounded-xl p-3">
                <p className="text-zinc-500 text-[10px] uppercase tracking-wide mb-1">{t('bookings.first_appointment')}</p>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-yellow-400" />
                  <p className="text-white text-sm font-medium">
                    {showSeriesResult.first_appointment.date} um {showSeriesResult.first_appointment.time}
                  </p>
                </div>
                {showSeriesResult.first_appointment.trainer_name && (
                  <p className="text-zinc-400 text-xs mt-1">
                    Mit: {showSeriesResult.first_appointment.trainer_name}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-zinc-700">
            <button
              onClick={() => { setShowSeriesResult(null); onBack(); }}
              className="w-full py-3 bg-yellow-500 rounded-xl text-black font-bold"
              data-testid="series-result-close"
            >
              Schliessen
            </button>
          </div>
        </div>
      )}

      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <div className="absolute inset-0 bg-black/95 z-50 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-zinc-700">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowWaitlistModal(false)} 
                className="p-2 bg-zinc-800 rounded-lg"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
              <div className="flex-1">
                <h2 className="text-white font-bold">{t('bookings.waitlist_title')}</h2>
                <p className="text-zinc-500 text-xs">{t('bookings.waitlist_desc')}</p>
              </div>
              <Bell className="w-6 h-6 text-purple-400" />
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Service Info */}
            <div className="bg-zinc-800/50 rounded-xl p-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-white font-medium">{selectedService?.name}</p>
                  {selectedTrainer && (
                    <p className="text-zinc-400 text-xs">
                      mit {selectedTrainer.name || `${selectedTrainer.first_name} ${selectedTrainer.last_name}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Date Mode Selection */}
            <div className="space-y-2">
              <p className="text-zinc-400 text-xs font-medium">{t('bookings.time_period')}</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setWaitlistDateMode('recurring')}
                  className={`py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    waitlistDateMode === 'recurring' 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <RefreshCw className="w-4 h-4" />
                  Wiederkehrend
                </button>
                <button
                  onClick={() => setWaitlistDateMode('specific')}
                  className={`py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    waitlistDateMode === 'specific' 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Bestimmter Tag
                </button>
              </div>
            </div>
            
            {/* Recurring: Weekday Selection */}
            {waitlistDateMode === 'recurring' && (
              <div className="space-y-2">
                <p className="text-zinc-400 text-xs font-medium">{t('bookings.desired_weekdays')}</p>
                <div className="flex flex-wrap gap-2">
                  {weekdayOptions.map(day => (
                    <button
                      key={day.value}
                      onClick={() => {
                        if (day.value === 'daily') {
                          setWaitlistPreferredDays(['daily']);
                        } else {
                          let newDays = waitlistPreferredDays.filter(d => d !== 'daily');
                          if (newDays.includes(day.value)) {
                            newDays = newDays.filter(d => d !== day.value);
                          } else {
                            newDays.push(day.value);
                          }
                          if (newDays.length === 0) newDays = ['daily'];
                          setWaitlistPreferredDays(newDays);
                        }
                      }}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        waitlistPreferredDays.includes(day.value)
                          ? 'bg-purple-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Specific Date Selection */}
            {waitlistDateMode === 'specific' && (
              <div className="space-y-2">
                <p className="text-zinc-400 text-xs font-medium">{t('bookings.select_date')}</p>
                <input
                  type="date"
                  value={waitlistSpecificDate}
                  onChange={(e) => setWaitlistSpecificDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-3 bg-zinc-800 rounded-xl text-white"
                />
              </div>
            )}
            
            {/* Time Preferences */}
            <div className="space-y-2">
              <p className="text-zinc-400 text-xs font-medium">{t('bookings.preferred_time')}</p>
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={() => toggleWaitlistPreference('any')}
                  className={`py-3 rounded-xl text-xs font-medium flex flex-col items-center gap-1 transition-all ${
                    waitlistPreferences.includes('any')
                      ? 'bg-purple-500 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <Zap className="w-4 h-4" />
                  Egal
                </button>
                <button
                  onClick={() => toggleWaitlistPreference('morning')}
                  className={`py-3 rounded-xl text-xs font-medium flex flex-col items-center gap-1 transition-all ${
                    waitlistPreferences.includes('morning')
                      ? 'bg-purple-500 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <Sun className="w-4 h-4" />
                  Morgens
                </button>
                <button
                  onClick={() => toggleWaitlistPreference('afternoon')}
                  className={`py-3 rounded-xl text-xs font-medium flex flex-col items-center gap-1 transition-all ${
                    waitlistPreferences.includes('afternoon')
                      ? 'bg-purple-500 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <Sunset className="w-4 h-4" />
                  Mittags
                </button>
                <button
                  onClick={() => toggleWaitlistPreference('evening')}
                  className={`py-3 rounded-xl text-xs font-medium flex flex-col items-center gap-1 transition-all ${
                    waitlistPreferences.includes('evening')
                      ? 'bg-purple-500 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  <Moon className="w-4 h-4" />
                  Abends
                </button>
              </div>
            </div>
            
            {/* Custom Time Range */}
            <div className="space-y-2">
              <p className="text-zinc-400 text-xs font-medium">{t('bookings.custom_time_range')}</p>
              <div className="flex gap-2 items-center">
                <input
                  type="time"
                  value={customTimeStart}
                  onChange={(e) => setCustomTimeStart(e.target.value)}
                  className="flex-1 px-3 py-2 bg-zinc-800 rounded-lg text-white text-sm"
                />
                <span className="text-zinc-500">-</span>
                <input
                  type="time"
                  value={customTimeEnd}
                  onChange={(e) => setCustomTimeEnd(e.target.value)}
                  className="flex-1 px-3 py-2 bg-zinc-800 rounded-lg text-white text-sm"
                />
                <button
                  onClick={addCustomTimeRange}
                  className="px-3 py-2 bg-purple-500/20 text-purple-400 rounded-lg text-sm"
                >
                  +
                </button>
              </div>
              {customTimeRanges.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {customTimeRanges.map((range, i) => (
                    <span 
                      key={i}
                      className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs flex items-center gap-1"
                    >
                      {range}
                      <button 
                        onClick={() => setCustomTimeRanges(customTimeRanges.filter((_, idx) => idx !== i))}
                        className="text-purple-400 hover:text-purple-200"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* Auto-Book Option */}
            <div className="bg-zinc-800/50 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  <div>
                    <p className="text-white text-sm font-medium">{t('bookings.auto_book')}</p>
                    <p className="text-zinc-500 text-xs">{t('bookings.auto_book_desc')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setWaitlistAutoBook(!waitlistAutoBook)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    waitlistAutoBook ? 'bg-purple-500' : 'bg-zinc-700'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    waitlistAutoBook ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              
              {waitlistAutoBook && (
                <div className="pt-3 border-t border-zinc-700">
                  <p className="text-zinc-400 text-xs mb-2">{t('bookings.auto_book_lead_time')}</p>
                  <div className="flex gap-2">
                    {[2, 6, 12, 24].map(hours => (
                      <button
                        key={hours}
                        onClick={() => setWaitlistAutoBookHours(hours)}
                        className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
                          waitlistAutoBookHours === hours
                            ? 'bg-purple-500 text-white'
                            : 'bg-zinc-700 text-zinc-400'
                        }`}
                      >
                        {hours}h
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-zinc-700">
            <button
              onClick={handleJoinWaitlist}
              disabled={joiningWaitlist || (waitlistDateMode === 'specific' && !waitlistSpecificDate)}
              className="w-full py-4 bg-purple-500 rounded-xl text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {joiningWaitlist ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Bell className="w-5 h-5" />
                  Zur Warteliste hinzufügen
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Add-on Service Popup - Shows after successful booking */}
      {showAddonPopup && lastBookedAppointment && (
        <div className="absolute inset-0 bg-black/95 z-50 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-zinc-700">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => {
                  setShowAddonPopup(false);
                  onBack();
                }} 
                className="p-2 bg-zinc-800 rounded-lg"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
              <div>
                <h2 className="text-white font-bold">🎁 Zusatzangebot</h2>
                <p className="text-zinc-400 text-xs">{t('bookings.enhance_training')}</p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Success Message */}
            <div className="bg-yellow-500/20 rounded-xl p-4 border border-yellow-500/30 text-center">
              <Check className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <p className="text-yellow-400 font-bold">{t('bookings.booked_success')}</p>
              <p className="text-zinc-400 text-xs mt-1">
                {lastBookedAppointment.service_name} am {lastBookedAppointment.date} um {lastBookedAppointment.time} Uhr
              </p>
              {mobileCalendarLinks && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="mobile-calendar-links">
                  <button
                    onClick={() => window.open(mobileCalendarLinks.googleUrl, '_blank')}
                    className="flex-1 py-2 bg-yellow-500 text-black rounded-lg text-xs font-semibold"
                    data-testid="mobile-calendar-google"
                  >
                    Google
                  </button>
                  <button
                    onClick={() => window.open(mobileCalendarLinks.outlookUrl, '_blank')}
                    className="flex-1 py-2 bg-blue-500 text-black rounded-lg text-xs font-semibold"
                    data-testid="mobile-calendar-outlook"
                  >
                    Outlook
                  </button>
                  <button
                    onClick={() => { window.location.href = mobileCalendarLinks.appleUrl; }}
                    className="flex-1 py-2 bg-zinc-200 text-black rounded-lg text-xs font-semibold"
                    data-testid="mobile-calendar-apple"
                  >
                    Apple/iCal
                  </button>
                </div>
              )}
            </div>
            
            {loadingAddon ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-8 h-8 text-zinc-400 animate-spin" />
              </div>
            ) : addonAvailability?.available ? (
              <>
                {/* Add-on Offer */}
                <div className="bg-gradient-to-br from-cyan-500/20 to-blue-600/10 rounded-xl p-4 border border-cyan-500/30">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-cyan-500/30 rounded-lg flex items-center justify-center">
                      <span className="text-xl">❄️</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-bold">{addonAvailability.addon_service?.name}</h3>
                      <p className="text-zinc-400 text-xs">{t('bookings.recovery_tip')}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2 text-zinc-300">
                      <Clock className="w-3.5 h-3.5 text-cyan-400" />
                      <span>{addonAvailability.addon_service?.duration || 3} Min • {addonAvailability.addon_start_time} - {addonAvailability.addon_end_time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-cyan-400 font-medium">
                      <Zap className="w-3.5 h-3.5" />
                      <span>
                        Kombi-Preis: CHF {addonAvailability.combo_price?.toFixed(2)} 
                        {addonAvailability.savings > 0 && (
                          <span className="text-zinc-500 line-through ml-2">
                            CHF {addonAvailability.addon_service?.original_price?.toFixed(2)}
                          </span>
                        )}
                      </span>
                    </div>
                    {addonAvailability.savings > 0 && (
                      <div className="flex items-center gap-2 text-yellow-400 font-medium">
                        <span>💰 Du sparst CHF {addonAvailability.savings?.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  
                  <button 
                    className="w-full mt-3 py-2.5 bg-cyan-500 rounded-lg text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    onClick={bookAddonService}
                    disabled={bookingAddon}
                  >
                    {bookingAddon ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Wird gebucht...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Hinzufügen
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-4 text-zinc-500">
                <p>{t('bookings.no_addon')}</p>
              </div>
            )}
            
            {/* Skip Button */}
            <button 
              onClick={() => {
                setShowAddonPopup(false);
                onBack();
              }}
              className="w-full py-3 bg-zinc-800 rounded-xl text-zinc-400 font-medium text-sm"
            >
              Nein danke, weiter ohne Zusatz
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingNewScreen;
